import moduleSynergyJson from "./generated/moduleSynergy.json";
import { ALL_MODULE_DEFINITIONS } from "./moduleDefinitions.js";
import { parseModuleEffect, type ModuleEffectData } from "../modules/ModuleEffect.js";

/**
 * **시너지 도감 카탈로그** — `data/moduleSynergy.tsv`가 SSoT. `npm run export:data`로 생성된
 * `generated/moduleSynergy.json`을 enable 필터 후 파이프(`|`) split + effectDsl(mini-DSL)을
 * 기존 parseModuleEffect로 ModuleEffectData로 변환한다. 시너지는 **여러 moduleId 조합 발견 보상**으로,
 * 단일 moduleId 숙련도(모듈 도감)와 다르다.
 *
 * 효과는 코드 하드코딩 0 — modules.tsv effect{Grade}와 동일한 mini-DSL을 재사용한다(`damageMul=1.08|...`).
 * displayName/description은 개발 참조용 (실제 UI는 i18n `synergyCodex.*` 키). 시너지 판정/적용은
 * [ModuleSynergyResolver]가, 발견 영속은 [ModuleSynergyController]가 소비한다.
 */
export type SynergyGrade = "common" | "advanced" | "rare" | "legendary";

/** grade desc 정렬용 가중치(legendary 최고). 동시발동 선정에서 priority 동률 시 tie-breaker. */
export const SYNERGY_GRADE_ORDER: Readonly<Record<SynergyGrade, number>> = {
  common: 0,
  advanced: 1,
  rare: 2,
  legendary: 3,
};

const VALID_GRADES = new Set<string>(Object.keys(SYNERGY_GRADE_ORDER));

/** 최초 발견 보상 — firstRewardDsl(`diamond=20|stone=150`) 파싱 결과. 빈 값이면 전부 0/미지정. */
export interface SynergyReward {
  readonly gold?: number;
  readonly diamond?: number;
  readonly stone?: number;
  readonly relic?: number;
}

export interface ModuleSynergyDefinition {
  readonly id: string;
  readonly displayName: string;
  readonly description: string;
  readonly grade: SynergyGrade;
  readonly requiredModuleIds: readonly string[];
  readonly forbiddenModuleIds: readonly string[];
  readonly requiredSlotTypes: readonly string[];
  readonly minActiveModules: number;
  readonly effect: ModuleEffectData;
  readonly firstReward: SynergyReward;
  readonly priority: number;
}

/**
 * 시너지 effectDsl 금지 키 — 발사체 수 폭증(countMul)·delayedCall 폭증(burst/burstDelayMs)으로 모바일
 * 성능 위험이 커 시너지 보정에는 금지한다(개별 모듈 effect와 구분). 파서가 조용히 무시하므로 별도 throw.
 */
const FORBIDDEN_EFFECT_KEYS: readonly (keyof ModuleEffectData)[] = [
  "countMul",
  "burst",
  "burstDelayMs",
];

/** firstRewardDsl(`key=number|key2=number`) → SynergyReward. 알 수 없는 키는 무시, 빈 값이면 {}. */
function parseSynergyReward(dsl: string | undefined): SynergyReward {
  const out: { gold?: number; diamond?: number; stone?: number; relic?: number } = {};
  if (dsl && dsl.trim()) {
    for (const pair of dsl.split("|")) {
      const eq = pair.indexOf("=");
      if (eq < 0) continue;
      const key = pair.slice(0, eq).trim();
      const n = Number(pair.slice(eq + 1).trim());
      if (!Number.isFinite(n) || n <= 0) continue;
      if (key === "gold" || key === "diamond" || key === "stone" || key === "relic") out[key] = n;
    }
  }
  return out;
}

/** `a|b|c` → ["a","b","c"] (빈 토큰 제거, 공백 trim). 빈 문자열이면 []. */
function splitIds(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return [];
  return raw
    .split("|")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** 현재 enable=true인 모듈 id 집합 — 시너지 requiredModuleId가 실존 활성 모듈인지 검증용. */
const ACTIVE_MODULE_IDS = new Set(ALL_MODULE_DEFINITIONS.map((d) => d.id));

/** generated/moduleSynergy.json 한 행의 raw 타입 (export:data wide 산출 — 테스트 주입용). */
export type SynergyRawRow = (typeof moduleSynergyJson)[number];

/**
 * 카탈로그 빌드 (모듈 로드 1회). 검증: id 중복 throw / grade 화이트리스트 / requiredModuleIds 빈값 제외 /
 * 비활성·미존재 requiredModuleId 포함 시 warning 후 제외 / forbidden 미존재는 warning 후 무시 /
 * effectDsl에 금지 키(countMul/burst/burstDelayMs) 있으면 throw. priority 내림차순 정렬.
 * rows는 테스트가 가짜 행을 주입할 수 있게 파라미터화(기본값 = generated json).
 */
export function buildSynergyDefinitions(
  rows: readonly SynergyRawRow[] = moduleSynergyJson,
): ModuleSynergyDefinition[] {
  const seen = new Set<string>();
  const out: ModuleSynergyDefinition[] = [];
  for (const row of rows) {
    if (!row.enable) continue;
    const id = row.id;
    if (seen.has(id)) throw new Error(`[moduleSynergy] duplicate synergy id: ${id}`);
    seen.add(id);

    if (!VALID_GRADES.has(row.grade)) {
      console.warn(`[moduleSynergy] ${id}: unknown grade '${row.grade}' — excluded`);
      continue;
    }

    const requiredModuleIds = splitIds(row.requiredModuleIds);
    if (requiredModuleIds.length === 0) {
      console.warn(`[moduleSynergy] ${id}: empty requiredModuleIds — excluded`);
      continue;
    }
    const missing = requiredModuleIds.filter((m) => !ACTIVE_MODULE_IDS.has(m));
    if (missing.length > 0) {
      console.warn(
        `[moduleSynergy] ${id}: requiredModuleIds not active [${missing.join(", ")}] — excluded`,
      );
      continue;
    }

    const forbiddenModuleIds = splitIds(row.forbiddenModuleIds).filter((m) => {
      if (ACTIVE_MODULE_IDS.has(m)) return true;
      console.warn(`[moduleSynergy] ${id}: forbiddenModuleId '${m}' unknown — ignored`);
      return false;
    });

    const effect = parseModuleEffect(row.effectDsl);
    const banned = FORBIDDEN_EFFECT_KEYS.filter((k) => effect[k] !== undefined);
    if (banned.length > 0) {
      throw new Error(
        `[moduleSynergy] ${id}: forbidden effect key(s) [${banned.join(", ")}] — countMul/burst are banned in synergy effectDsl`,
      );
    }

    out.push({
      id,
      displayName: row.displayName,
      description: row.description,
      grade: row.grade as SynergyGrade,
      requiredModuleIds,
      forbiddenModuleIds,
      requiredSlotTypes: splitIds(row.requiredSlotTypes),
      minActiveModules: Math.max(0, Math.floor(row.minActiveModules)),
      effect,
      firstReward: parseSynergyReward(row.firstRewardDsl),
      priority: Math.floor(row.priority),
    });
  }
  out.sort((a, b) => b.priority - a.priority);
  return out;
}

export const ALL_MODULE_SYNERGY_DEFINITIONS: readonly ModuleSynergyDefinition[] =
  buildSynergyDefinitions();

const BY_ID = new Map(ALL_MODULE_SYNERGY_DEFINITIONS.map((s) => [s.id, s]));

export function synergyDefinitionOf(id: string): ModuleSynergyDefinition | undefined {
  return BY_ID.get(id);
}
